import "./entityList.scss";

import { List } from "immutable";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import EmptyState from "../../components/emptyState";
import FilterRow from "../../components/filter/FilterRow";
import FormButton from "../../components/form/FormButton";
import FormDropdown from "../../components/form/FormDropdown";
import SplitButton from "../../components/form/SplitButton";
import Icon from "../../components/interface/Icon";
import LongPressCapture from "../../components/longPressCapture";
import { withScreenContext } from "../../components/screen/ScreenContext";
import TabLock from "../../components/tabLock";
import ToolbarGroup from "../../components/toolbar/ToolbarGroup";
import ToolbarIconButton from "../../components/toolbar/ToolbarIconButton";
import ToolbarTextButton from "../../components/toolbar/ToolbarTextButton";
import ToolbarView from "../../components/toolbar/ToolbarView";
import TreeView from "../../components/treeView";
import { DROP_Z_INDEX_ENTITY_LIST } from "../../constants/dropZIndex";
import { matchSegmentOptions } from "../../constants/filter";
import filterMatchOptions from "../../constants/filterMatchOptions";
import { ENTITY_LIST_VIEW, GLOBAL_FILTER_ID } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import PubSubTopics from "../../constants/pubSubTopics";
import dialogs from "../../dialogs";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import batchUtils from "../../modules/batchUtils";
import { typeSelectors } from "../../modules/definition/types";
import { filterOperations, filterSelectors } from "../../modules/filters";
import { itemActionTypes, itemOperations, itemSelectors } from "../../modules/items";
import { superAndChildrenSelectors } from "../../modules/superAndChildrenById";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { shouldAutoEditItems } from "../../modules/undoRedo";
import { PropTypeShapes } from "../../types";
import {
  chooseValueForIos,
  chooseValueForTablet,
  mergeDispatchToProps,
  wrapEditingComponent
} from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { updateSelectedItemsUsingUpdaters } from "../../utils/selection";
import { trackEvent } from "../../utils/trackAnalytics";
import { getDisplayItemsTreeOfType, getFlatTreeItemIds } from "./selectors";

const ENTITY_TREE_VIEW_ID = "ENTITY_TREE_VIEW_ID";

const getItemIdsFromDropAction = action => {
  return batchUtils.isBatchAction(action) ? action.actions.reduce((ids, subA) => ids.concat(subA.ids), []) : action.ids;
};

class EntityTreeView extends React.PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onItemClick = this.onItemClick.bind(this);
    this.onItemDoubleClick = this.onItemDoubleClick.bind(this);
    this.onDispatchDropAction = this.onDispatchDropAction.bind(this);
    this.onCanDrop = this.onCanDrop.bind(this);
    this.onCanDrag = this.onCanDrag.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.getSetIndexAction = this.getSetIndexAction.bind(this);
    this.canTypeHaveSuperType = this.canTypeHaveSuperType.bind(this);
    this.getDescendantsOfItems = this.getDescendantsOfItems.bind(this);
    this.selectOption = this.selectOption.bind(this);
    this.onCreateClick = this.onCreateClick.bind(this);
    this.onFilterClick = this.onFilterClick.bind(this);
    this.onCtrlModifierKeyDown = this.onCtrlModifierKeyDown.bind(this);
    this.onCtrlModifierKeyUp = this.onCtrlModifierKeyUp.bind(this);
    this.renderMatchSegment = this.renderMatchSegment.bind(this);
    this.onEditClick = this.onEditClick.bind(this);
    this.renderEditModeToolbar = this.renderEditModeToolbar.bind(this);
    this.onEditItems = this.onEditItems.bind(this);
    this.onDeleteItems = this.onDeleteItems.bind(this);
    this.onToggleItem = this.onToggleItem.bind(this);

    //#!if iOS === true
// this.onLongPress = this.onLongPress.bind(this);
    //#!endif

    this.state = {
      ctrlModifierKeyPressed: false
    };

    this._lastAction = null;
  }

  componentWillMount() {}

  componentDidMount() {
    this._pubSubCtrlModifierKeyDownToken = PubSub.subscribe(
      PubSubTopics.ctrlModifierKeyDown,
      this.onCtrlModifierKeyDown
    );
    this._pubSubCtrlModifierKeyUpToken = PubSub.subscribe(PubSubTopics.ctrlModifierKeyUp, this.onCtrlModifierKeyUp);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.visibleTypeId !== this.props.visibleTypeId) {
      this._lastClickItemId = null;
      this._firstShiftClickSelection = null;
    }
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubCtrlModifierKeyDownToken);
    PubSub.unsubscribe(this._pubSubCtrlModifierKeyUpToken);
  }

  onDragLeave() {}

  onDispatchDropAction(action) {
    this.props.onDispatchAction(action);
    //#!if iOS !== true
    const droppedIds = getItemIdsFromDropAction(action);
    if (droppedIds && droppedIds.length > 0) {
      this.props.onSelectItems(droppedIds);
    }
    //#!endif
  }

  onItemClick(itemId, modifiers = {}) {
    const isIos = preprocessorQuery.isIos();
    if (modifiers.SHIFT && this._lastClickItemId) {
      // If this is the first shift click since some other click, record the current selection
      // as the starting point for any subsequent shift clicks
      if (!this._firstShiftClickSelection) {
        this._firstShiftClickSelection = this.props.selectedItemIds;
      }
      // Select everything from last click to current position
      const a = this.props.flatItemIds.findIndex(id => id === this._lastClickItemId);
      const b = this.props.flatItemIds.findIndex(id => id === itemId);
      const low = Math.min(a, b);
      const high = Math.max(a, b);
      const newIds = this.props.flatItemIds.slice(low, high + 1).toArray();
      // Note that we can't simply add to selection, as we may want to select a lesser amount than was
      // selected with the previous shift click.
      // So instead we add this new selection to the original selection before the shift clicks began
      const allIds = this._firstShiftClickSelection.push(...newIds).toArray();
      this.props.onSelectItems(allIds);
      return;
    } else if (isIos && (this.props.screenContext.isEditMode || this.props.isEntityEditMode)) {
      // If editing an entity list in mobile
      if (this.props.selectedItemIds.includes(itemId)) {
        this.props.onRemoveFromSelection(itemId);
      } else {
        this.props.onAddToSelection(itemId);
      }
      return;
    }

    // If we have a non-shift click, record it so we can use it as the starting point for any subsequent
    // shift clicks
    this._lastClickItemId = itemId;
    // Erase the first shift click so we start again the next time we do a shift click
    this._firstShiftClickSelection = null;

    // And then go on and handle non-shift clicks normally!
    updateSelectedItemsUsingUpdaters(
      modifiers.CONTROL ||
        modifiers.COMMAND ||
        (isIos && (this.props.screenContext.isEditMode || this.props.isEntityEditMode)),
      itemId,
      this.props.selectedItemIds,
      this.props.onRemoveFromSelection,
      this.props.onAddToSelection,
      this.props.onSelectItems
    );
  }

  onItemDoubleClick() {
    if (this.props.onShowInspector) {
      this.props.onShowInspector();
    }
  }

  canDragAndDrop() {
    let allowDrag = true;
    //#!if iOS === true
// allowDrag = this.props.screenContext.isEditMode || this.props.isEntityEditMode;
    //#!endif
    return allowDrag;
  }

  onCanDrop(itemIds) {
    const allowDrag = this.canDragAndDrop();
    // The dropping items are all the items of the visible type
    // Check whether can find an item NOT in the list of the items of type
    return allowDrag && itemIds.find(id => this.props.itemIdsOfType.indexOf(id) === -1) === undefined;
  }

  onCanDrag(itemId) {
    return this.canDragAndDrop(itemId);
  }

  getSetIndexAction(typeId, itemIds, newIndex) {
    return itemOperations.setItemIndexForType(typeId, itemIds, newIndex);
  }

  canTypeHaveSuperType(childType, superType) {
    return this.props.superTypesById.get(childType).contains(superType);
  }

  getDescendantsOfItems(itemIds) {
    return superAndChildrenSelectors.getDescendantSetFromMapForIds(this.props.descendantsMap, itemIds);
  }

  selectOption(opt) {
    trackEvent("createItemFromEntityListSplitDropdown", "createItem", opt);
    this.props.onCreateClick(opt);
  }

  //#!if iOS === true
// onLongPress() {
// if (this.props.screenContext.enterEditMode && !this.props.isEntityEditMode) {
// this.props.screenContext.enterEditMode();
// }
// }
  //#!endif

  onCreateClick() {
    trackEvent("createItemFromEntityList", "createItem", this.props.visibleTypeId);
    this.props.onCreateClick(this.props.visibleTypeId);
  }

  onFilterClick(itemId, modifiers = {}) {
    const itemType = this.props.itemsById.get(itemId).type;
    const relationshipsFilter = this.props.viewFilter.relationshipsByType.get(itemType);
    let filterTerms = relationshipsFilter ? [...relationshipsFilter.terms] : [];
    const termIndex = filterTerms ? filterTerms.indexOf(itemId) : -1;

    const viewId = preprocessorQuery.isMobile() ? GLOBAL_FILTER_ID : this.props.activeViewId;

    if (termIndex >= 0) {
      filterTerms.splice(termIndex, 1);
      this.props.onFilterClick(viewId, itemType, filterTerms, filterMatchOptions.ANY);
    } else {
      if ((modifiers.CONTROL || modifiers.COMMAND) && !filterTerms.includes(itemId)) {
        filterTerms.push(itemId);
      }
      this.props.onFilterClick(
        viewId,
        itemType,
        modifiers.CONTROL || modifiers.COMMAND ? filterTerms : [itemId],
        filterMatchOptions.ANY
      );
    }
  }

  onCtrlModifierKeyDown() {
    this.setState({
      ctrlModifierKeyPressed: true
    });
  }

  onCtrlModifierKeyUp() {
    this.setState({
      ctrlModifierKeyPressed: false
    });
  }

  renderMatchSegment(filter, activeViewId, visibleTypeId) {
    const relationshipByTypeFilter = filter.relationshipsByType.get(visibleTypeId);
    const showFilter = relationshipByTypeFilter && relationshipByTypeFilter.terms.size > 1;
    return (
      <FilterRow key="match" collapsed={!showFilter}>
        <FormDropdown
          options={matchSegmentOptions}
          value={relationshipByTypeFilter ? relationshipByTypeFilter.match : null}
          onChange={
            relationshipByTypeFilter
              ? value =>
                this.props.onTypeReferenceFilterChanged(
                  activeViewId,
                  visibleTypeId,
                  relationshipByTypeFilter.terms,
                  value
                )
              : null
          }
          className="filterMatchDropdown"
        />
      </FilterRow>
    );
  }

  renderHelpText() {
    let helpText = null;
    //#!if iOS !== true && readonly !== true
// helpText = (
// <div className="entityFilterInstructions">
// <Icon icon="workviewFilterFilled" color="iconFade" />
// Filter the focused view
// <br />
// <Icon icon="workviewFilter" color="iconFade" />
// Hold <span className="keyboardKey">⌘cmd</span> or <span className="keyboardKey">ctrl</span> to add more filters
// </div>
// );
    //#!endif
    return helpText;
  }

  onEditClick() {
    this.props.onEnterEntityEditMode(this.props.visibleTypeId);
  }

  onDeleteItems() {
    dialogs.okCancelPrompt("Are you sure you want to delete this item?", result => {
      if (result) {
        this.props.onDeleteClick(this.props.selectedItemIds.toArray());
      }
    });
  }

  onEditItems() {
    //#!if tablet === true
// this.props.openIosInspectorPanel();
    //#!endif
  }

  renderEditModeToolbar() {
    // TODO: This toolbar is not complete, so I have added the TODO label to the center
    // to remind us to fix it. The center should normally be clear in mobile mode.
    return wrapEditingComponent(
      <ToolbarView domId="entityEditMode" isEditMode={true}>
        <ToolbarGroup position="leftTop">
          <ToolbarTextButton text="Done" onClick={this.props.onExitEntityEditMode} />
        </ToolbarGroup>
        <ToolbarGroup position="right">
          {this.props.selectedItemIds.size > 1 ? (
            <div className="inspectorSelectedItemCount">{this.props.selectedItemIds.size}</div>
          ) : null}
          <ToolbarIconButton
            icon="toolbarEdit"
            tooltip="Edit"
            active={false}
            disabled={!this.props.selectedItemIds.size}
            onClick={this.onEditItems}
            editMode={true}
          />
          <ToolbarIconButton
            icon="toolbarTrash"
            tooltip="Delete"
            active={false}
            disabled={!this.props.selectedItemIds.size}
            onClick={this.onDeleteItems}
            editMode={true}
          />
        </ToolbarGroup>
      </ToolbarView>
    );
  }

  updateItemFilterIcon(item) {
    let newItem = item.statusIcon === "workviewFilterFilled" ? item.merge({ statusIcon: "workviewFilter" }) : item;
    if (newItem.children) {
      newItem = newItem.set(
        "children",
        newItem.children.map(i => this.updateItemFilterIcon(i))
      );
    }
    return newItem;
  }

  updateItemDragHandle(item) {
    let newItem = item.merge({ statusIcon: "dragHandle" });
    if (newItem.children) {
      newItem = newItem.set(
        "children",
        newItem.children.map(i => this.updateItemDragHandle(i))
      );
    }
    return newItem;
  }

  getOptionGroups() {
    // TODO: May use the typeSelectors.getTypeIdsForEntityList

    // Options for dropdown to add an entity of super types
    let addEntityOptions = this.props.superTypeValues;
    // Don't show dropdown if the only allowed super type is the entity itself
    if (addEntityOptions.size == 1 && addEntityOptions.includes(this.props.typeValues)) {
      addEntityOptions = List();
    }
    // If showing dropdown, add the current entity to the top of the list
    if (addEntityOptions.size && this.props.typeValues && !addEntityOptions.includes(this.props.typeValues)) {
      addEntityOptions = addEntityOptions.unshift(this.props.typeValues);
    }

    const optionGroups = addEntityOptions.size
      ? List([
        {
          title: "",
          id: "addNew",
          options: addEntityOptions,
          showTitle: false,
          showSearch: false,
          onSelectOption: this.selectOption
        }
      ])
      : List();

    return optionGroups;
  }

  onToggleItem(itemId, isExpanding) {
    this.props.onToggleItem(this.props.visibleTypeId, itemId, isExpanding);
  }

  getEditButton() {
    return chooseValueForTablet(
      !this.props.screenContext || !this.props.screenContext.isEditMode ? (
        wrapEditingComponent(<FormButton label="Edit" onClick={this.onEditClick} secondary={true} />)
      ) : (
        <div className="entityHeaderPlaceholder" />
      ),
      null
    );
  }

  getSplitButtonCreate() {
    const optionGroups = this.getOptionGroups();
    const label = chooseValueForTablet("", "Create");
    return wrapEditingComponent(
      <SplitButton
        label={label}
        onClick={this.onCreateClick}
        id="addEntity"
        optionGroups={optionGroups}
        small={true}
        icon="sidebarAdd"
        pulse={this.props.pulsateIconId && this.props.pulsateIconId === interfaceIdentifiers.ENTITY_PANEL_ADD}
      />
    );
  }

  getTypeLabel() {
    return this.props.typeValues
      ? this.props.typeValues.pluralLabel
        ? this.props.typeValues.pluralLabel
        : this.props.typeValues.label
      : "";
  }

  getNormalHeader() {
    const createButton = this.getSplitButtonCreate();
    return (
      <div className="entityListHeader">
        {chooseValueForTablet(createButton, null)}
        <div className="entityListTitle">
          <Icon
            icon={this.props.typeValues ? this.props.typeValues.icon : "blank"}
            color={this.props.typeValues ? this.props.typeValues.color : "mono"}
          />
          <span>{this.getTypeLabel()}</span>
        </div>
        {chooseValueForTablet(this.getEditButton(), createButton)}
      </div>
    );
  }

  render() {
    const isIos = preprocessorQuery.isIos();
    const isTablet = preprocessorQuery.isTablet();
    const isEditMode = isIos ? this.props.screenContext.isEditMode || this.props.isEntityEditMode : false;
    let onItemStatusClick = this.onFilterClick;

    let items = this.props.items;

    //#!if iOS !== true
    if (this.state.ctrlModifierKeyPressed && items.count() > 0) {
      items = items.map(i => this.updateItemFilterIcon(i));
    }
    //#!else
// 
// if (isEditMode) {
// items = items.map(i => this.updateItemDragHandle(i));
// onItemStatusClick = () => {};
// }
    //#!endif

    const style = {};
    //#!if mobile === true
// style.paddingLeft = this.props.paddingLeft;
// style.paddingRight = this.props.paddingRight;
    //#!endif

    let result = (
      <div className={"entityList" + (this.props.visible ? " visible" : "")} style={style}>
        <TabLock autoFocus={false}>
          {isTablet && this.props.isEntityEditMode ? this.renderEditModeToolbar() : this.getNormalHeader()}
          {this.props.items.count() === 0 ? (
            <EmptyState
              svg={
                ["person", "map", "star"].includes(this.props.typeValues.icon) ? this.props.typeValues.icon : `default`
              }
              type="sidePanel"
            >
              <div key="content">
                <p>Looks like you haven&apos;t added any {this.props.typeValues.pluralLabel} yet.</p>
                {wrapEditingComponent(
                  <FormButton
                    primary={true}
                    label={`Create New ${this.props.typeValues.label}`}
                    onClick={this.onCreateClick}
                  />
                )}
              </div>
            </EmptyState>
          ) : (
            <Fragment>
              {this.renderMatchSegment(this.props.viewFilter, this.props.activeViewId, this.props.visibleTypeId)}
              <TreeView
                rootItems={items}
                selectedItemIds={this.props.selectedItemIds}
                onItemStatusClick={onItemStatusClick}
                onItemClick={this.onItemClick}
                onItemDoubleClick={this.onItemDoubleClick}
                onLabelChange={this.props.onLabelChange}
                expandedItems={this.props.expandedItems}
                onToggleItem={this.onToggleItem}
                itemIdsOfType={this.props.itemIdsOfType}
                itemIdsByType={this.props.itemIdsByType}
                onDragLeave={this.onDragLeave}
                onDispatchDropAction={this.onDispatchDropAction}
                onCanDrop={this.onCanDrop}
                onCanDrag={this.onCanDrag}
                viewId={ENTITY_TREE_VIEW_ID}
                width={this.props.containerRect.width}
                targetItemType={this.props.visibleTypeId}
                itemsById={this.props.itemsById}
                getSuperAction={itemOperations.setSuper}
                getIndexAction={this.getSetIndexAction}
                //#!if readonly !== true
// superActionType={itemActionTypes.SET_SUPER}
// indexActionType={itemActionTypes.SET_ITEM_INDEX_FOR_TYPE}
                //#!endif
                canTypeHaveSuperType={this.canTypeHaveSuperType}
                getDescendantsOfItems={this.getDescendantsOfItems}
                autoEditBlankNodes={this.props.autoEditBlankNodes}
                isEditMode={isEditMode}
                dropZIndex={DROP_Z_INDEX_ENTITY_LIST}
                pulsateIconId={this.props.pulsateIconId}
              />
            </Fragment>
          )}
        </TabLock>
        {this.renderHelpText()}
      </div>
    );

    result = chooseValueForIos(
      <LongPressCapture
        disabled={this.props.screenContext && this.props.screenContext.isEditMode}
        onLongPress={this.onLongPress}
      >
        {result}
      </LongPressCapture>,
      result
    );

    return result;
  }
}

const mapStateToProps = (state, props) => {
  const visibleTypeId = props.typeId || appStateSelectors.getVisibleTypeListId(state.present);
  const activeViewId = appStateSelectors.getActiveViewId(state.present);
  const filterViewId = preprocessorQuery.isMobile() ? GLOBAL_FILTER_ID : activeViewId;

  return {
    items: getDisplayItemsTreeOfType(state.present, visibleTypeId, filterViewId),
    flatItemIds: getFlatTreeItemIds(state.present, visibleTypeId, filterViewId),
    itemIdsOfType: itemSelectors.getItemIdsOfTypesWithParentTypes(state.present, [visibleTypeId]),
    superTypesById: typeSelectors.getSuperTypesById(state.present),
    visibleTypeId: visibleTypeId,
    visible: Boolean(visibleTypeId),
    itemsById: itemSelectors.getItemsById(state.present),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    expandedItems: appStateSelectors.getExpandedEntityItems(state.present, visibleTypeId),
    typeValues: visibleTypeId ? typeSelectors.getItemTypeValuesForGuid(state.present, visibleTypeId) : null,
    superTypeValues: typeSelectors.getSuperTypeValues(state.present, visibleTypeId),
    activeViewId: activeViewId,
    viewFilter: filterSelectors.getFilterWithId(state.present, filterViewId),
    // Disable auto-edit when applying a REDO action
    autoEditBlankNodes: shouldAutoEditItems(state),
    isEntityEditMode: uiStateSelectors.getIsEntityEditMode(state.present),
    itemIdsByType: itemSelectors.getItemIdsByType(state.present),
    descendantsMap: superAndChildrenSelectors.getDescendantsMapFromState(state.present),
    pulsateIconId: uiStateSelectors.getPulsateIconId(state.present)
  };
};

const readonlyActions = {
  onSelectItems: appStateOperations.selectItems,
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onFilterClick: filterOperations.changeTypeReferenceFilter,
  onShowInspector: uiStateOperations.showInspectorModal,
  onTypeReferenceFilterChanged: filterOperations.changeTypeReferenceFilter,
  onEnterEntityEditMode: batchOperations.enterEntityEditModeAndClearSelection,
  onExitEntityEditMode: batchOperations.exitEntityEditModeAndClearSelection,
  onToggleItem: appStateOperations.toggleEntityListItem
};

const editingActions = {
  onCreateClick: batchOperations.createAndSelectItem,
  onLabelChange: itemOperations.editLabel,
  onDispatchAction: a => a,
  onDeleteClick: itemOperations.deleteItems,
  onShowInspector: appStateOperations.showRightPanel,
  //#!if tablet === true
// openIosInspectorPanel: appStateOperations.openIosInspectorPanel
  //#!endif
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

EntityTreeView.propTypes = {
  items: PropTypes.object.isRequired,
  flatItemIds: ImmutablePropTypes.listOf(PropTypes.string),
  itemIdsOfType: PropTypes.object.isRequired,
  superTypesById: ImmutablePropTypes.map.isRequired,
  onDispatchAction: PropTypes.func.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onFilterClick: PropTypes.func.isRequired,
  onShowInspector: PropTypes.func,
  openIosInspectorPanel: PropTypes.func,
  onCreateClick: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  visibleTypeId: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  itemIdsByType: ImmutablePropTypes.map.isRequired,
  descendantsMap: ImmutablePropTypes.map.isRequired,
  typeValues: PropTypeShapes.itemType,
  superTypeValues: ImmutablePropTypes.list,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string),
  autoEditBlankNodes: PropTypes.bool.isRequired,
  activeViewId: PropTypes.string.isRequired,
  viewFilter: PropTypeShapes.filter.isRequired,
  onTypeReferenceFilterChanged: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  isEntityEditMode: PropTypes.bool,
  onEnterEntityEditMode: PropTypes.func,
  onExitEntityEditMode: PropTypes.func,
  expandedItems: ImmutablePropTypes.listOf(PropTypes.string),
  onToggleItem: PropTypes.func.isRequired,

  pulsateIconId: PropTypes.string,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object,
  //#!endif

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number
};

let exportView = connect(mapStateToProps, mapDispatchToProps)(withContainerContext(EntityTreeView, ENTITY_LIST_VIEW));

//#!if iOS === true
// exportView = withScreenContext(exportView);
//#!endif

export default exportView;
